import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSwitchOrganizationUseCase } from "@/infrastructure/di";
import { getAiAgentSessionService } from "@/infrastructure/di/services";
import { NON_ORG_SCOPED_QUERY_KEYS } from "@/application/queries";
import { ROUTES } from "@/app/routes";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useToastStore } from "@/stores/useToastStore";
import { resetAllFilterStores } from "@/stores/useResetAllFilterStore";

/** Routes with dynamic entity params where staying after org switch would 404. */
const DETAIL_ROUTE_PATTERNS = [
  /^\/beneficiaries\/[^/]+/,
  /^\/projects\/[^/]+/,
  /^\/investments\/[^/]+/,
  /^\/organization\/templates\/[^/]+/,
];

export const useSwitchOrganization = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const aiAgentSessionService = getAiAgentSessionService();

  return useMutation({
    mutationFn: async (organizationId: string | number) => {
      const result = await getSwitchOrganizationUseCase().execute(String(organizationId));
      if (!result.success) throw new Error(result.error);
      return { organizationId: String(organizationId) };
    },
    onSuccess: ({ organizationId }) => {
      /**
       * Detail pages embed the old org's entity UUID in their query key. We
       * navigate away so the new org's dashboard becomes the active route, but
       * React Suspense (SuspenseOutlet on lazy routes) keeps the old detail
       * page mounted until the new route loads — during that window the new
       * orgId triggers a refetch for the OLD UUID and 404s. Not a regression;
       * the previous implementation had the same behavior. Tracked as
       * follow-up — would require either a global "switching" flag or
       * dropping orgId from detail query keys.
       */
      const isDetailRoute = DETAIL_ROUTE_PATTERNS.some((p) => p.test(location.pathname));
      if (isDetailRoute) {
        navigate(ROUTES.DASHBOARD, { replace: true });
      } else if (location.search) {
        /**
         * Main listings keep their filters + pagination in the URL. Those
         * params are scoped to the previous org, so drop the query string
         * (staying on the same list route) — the store reset below no longer
         * covers them.
         */
        navigate(location.pathname, { replace: true });
      }

      queryClient.invalidateQueries({
        predicate: (query) => {
          const top = query.queryKey[0];
          if (typeof top !== "string") return true;
          return !NON_ORG_SCOPED_QUERY_KEYS.includes(
            top as (typeof NON_ORG_SCOPED_QUERY_KEYS)[number],
          );
        },
      });
      resetAllFilterStores();
      useOrganizationStore.getState().setSelectedOrganizationUuid(organizationId);
      aiAgentSessionService.clearSession();
      useToastStore.getState().clearAll();
    },
    onError: (error: Error) => {
      useToastStore.getState().showToast({
        message: error.message || t("organization.switch_failed"),
        type: "error",
      });
    },
  });
};
